import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BarcodeDisplay = ({ barcode, loading, onGenerate, onRegenerate }) => {
  const handleDownload = () => {
    const svg = document.getElementById('member-qr-code');
    const svgData = new XMLSerializer()?.serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas?.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img?.width;
      canvas.height = img?.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas?.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      downloadLink.download = `gym-barcode-${barcode?.barcode_data}.png`;
      downloadLink.href = pngFile;
      downloadLink?.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border">
        <div className="text-center py-12">
          <Icon name="Loader" size={48} className="animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Loading your barcode...</p>
        </div>
      </div>
    );
  }

  if (!barcode) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border">
        <div className="text-center py-12">
          <Icon name="QrCode" size={64} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-xl font-bold mb-2">No Barcode Generated</h3>
          <p className="text-muted-foreground mb-6">
            Generate your unique gym membership barcode to enable quick check-ins
          </p>
          <Button
            onClick={onGenerate}
            iconName="Plus"
          >
            Generate My Barcode
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg p-8 border border-border">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">Your Membership Barcode</h2>
        <p className="text-muted-foreground">
          Show this code at the gym entrance for quick check-in
        </p>
      </div>
      {/* QR Code Display */}
      <div className="flex justify-center mb-8 p-8 bg-white rounded-lg">
        <QRCodeSVG
          id="member-qr-code"
          value={barcode?.barcode_data}
          size={256}
          level="H"
          includeMargin
        />
      </div>
      {/* Barcode Info */}
      <div className="space-y-4 mb-8">
        <div className="p-4 bg-muted rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Barcode ID</p>
          <p className="font-mono text-sm break-all">{barcode?.barcode_data}</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Type</p>
            <p className="text-sm font-medium capitalize">
              {barcode?.barcode_type?.replace('_', ' ')}
            </p>
          </div>
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Status</p>
            <span
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                barcode?.is_active
                  ? 'bg-success/10 text-success' :'bg-destructive/10 text-destructive'
              }`}
            >
              <Icon name={barcode?.is_active ? 'CheckCircle' : 'XCircle'} size={12} />
              {barcode?.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Generated</p>
            <p className="text-sm font-medium">
              {new Date(barcode?.generated_at)?.toLocaleDateString()}
            </p>
          </div>
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Scan Count</p>
            <p className="text-sm font-medium">{barcode?.scan_count || 0} times</p>
          </div>
        </div>

        {barcode?.last_scanned_at && (
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Last Scanned</p>
            <p className="text-sm font-medium">
              {new Date(barcode?.last_scanned_at)?.toLocaleString()}
            </p>
          </div>
        )}
      </div>
      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Button
          onClick={handleDownload}
          variant="outline"
          iconName="Download"
        >
          Download
        </Button>
        <Button
          onClick={handlePrint}
          variant="outline"
          iconName="Printer"
        >
          Print
        </Button>
        <Button
          onClick={onRegenerate}
          variant="outline"
          iconName="RefreshCw"
        >
          Regenerate
        </Button>
      </div>
      {/* Instructions */}
      <div className="mt-8 p-4 bg-muted rounded-lg">
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          <Icon name="Info" size={18} />
          How to Use
        </h3>
        <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
          <li>Save this barcode to your phone for easy access</li>
          <li>Show the barcode at the gym entrance scanner</li>
          <li>The system will automatically log your check-in</li>
          <li>Keep your barcode secure and do not share with others</li>
        </ul>
      </div>
    </div>
  );
};

export default BarcodeDisplay;